import React, { useEffect, useState, useMemo, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Button, Typography, Spinner } from "@kohler-dsm-library/kohler-core"
import { getProductTabData } from "@/responseProcessors/searchResponseProcessor/productTab"
import { getUserPersona, getShortenedUrl } from "@/utils/helper"
import { useTranslation } from "next-i18next"
import _isEmpty from "lodash/isEmpty"
import useProductFavorites from "@/hooks/useProductFavorites"
import SaveToList from "@/components/SaveToList"
import ListConfirmationModal from "@/components/ListConfirmationModal"
import { showToast, selectGenericState } from "@/store/features/genericSlice"
import { compareState } from "@/store/features/compareSlice"
import {
  selectFavoritesState,
  addProduct,
  getMyAccountListsWithLineItems,
  removeItems,
} from "@/store/features/favoritesSlice"
import {
  selectAuthState,
  setAuthModalVisibility,
} from "@/store/features/authSlice"
import { useRouter } from "next/router"
import {
  dataLayerHeartClickSaveListButton,
  dataLayerSavetolistGuest,
} from "@/components/SaveToList/analytics"
import {
  WISHLIST_DELETE,
  WISHLIST_SAVE,
} from "@/components/SaveToList/constant"
import Modal from "@/components/core/Modal/Modal"
import dynamic from "next/dynamic"

const QuickView = dynamic(() => import("@/components/Shared/QuickView"))

const productTileHoc = WrappedComponent => {
  return function WithProductCardWrapper({
    data,
    isSterling,
    handleOnClickPartsDiagramLink,
    swatchCount,
    index,
    searchResultDataLayerObj,
    brandSwitcherItems,
    type,
    paginationState,
    quickViewAuthData,
  }) {
    const persona = getUserPersona()
    const router = useRouter()
    const [width] = typeof window !== "undefined" ? [window.innerWidth] : [1024]
    const locale = router.locale
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const {
      globalConfig = {},
      featureFlags = {},
      toaster,
    } = useSelector(selectGenericState)
    const stableSwatchCount = useMemo(() => swatchCount, [swatchCount])
    const { ENABLE_SHOPPING_LISTS_V2: isSaveProductToList = false } =
      featureFlags
    const compareData = useSelector(compareState)
    const { userListItemsSkus = {} } = useSelector(selectFavoritesState)
    const favorites = useSelector(
      state => state.favorites.favorites?.lineItems || [],
      (prev, next) => JSON.stringify(prev) === JSON.stringify(next)
    )
    const cartData = useSelector(state => state.cart.data)
    const { getFavIconStatus } = useProductFavorites()
    const { isAuth: isAuthenticated } = useSelector(selectAuthState)

    const [mainProps, setMainProps] = useState({})
    const [compareErrorMessage, setCompareErrorMessage] = useState("")
    const [compareChecked, setCompareChecked] = useState(false)
    const [productSavedToList, setProductSavedToList] = useState(false)
    const [openModal, setOpenModal] = useState(false)
    const [deleteFromList, setDeleteFromList] = useState(false)
    const [showAddToList, setShowAddToList] = useState(false)
    const [loginFavouriteCallback, setLoginFavouriteCallback] = useState(null)
    const [toasterContent, setToasterContent] = useState({
      successMessage: "",
      unfavoriteSuccessMessage: "",
    })
    const [favIconState, setFavIconState] = useState(false)
    const [isFavorite, setIsFavorite] = useState(false)
    const [colorSku, setColorSku] = useState(data.sku_s)
    const [cardLoader, setCardLoader] = useState(false)
    const [showQuickViewModal, setShowQuickViewModal] = useState(false)
    const [scrollY, setScrollY] = useState(0)

    const saveButtonRef = useRef(null)

    useEffect(() => {
      setToasterMessage()
      setIsFavorite(
        favorites?.find(vl => vl.productId === data["ctId_s"]) ? true : false
      )
    }, [])

    useEffect(() => {
      if (typeof window === "undefined") return
      const clearPlpLoader = () => {
        setCardLoader(false)
      }
      const onPageShow = e => {
        if (e && e.persisted) {
          clearPlpLoader()
        } else {
          const overlay = document.querySelector(".spinner-overlay")
          if (overlay) clearPlpLoader()
        }
      }
      const onVisibility = () => {
        if (document.visibilityState === "visible") {
          const overlay = document.querySelector(".spinner-overlay")
          if (overlay) clearPlpLoader()
        }
      }
      const onPopState = () => {
        const overlay = document.querySelector(".spinner-overlay")
        if (overlay) clearPlpLoader()
      }
      window.addEventListener("pageshow", onPageShow)
      document.addEventListener("visibilitychange", onVisibility)
      window.addEventListener("popstate", onPopState)
      return () => {
        window.removeEventListener("pageshow", onPageShow)
        document.removeEventListener("visibilitychange", onVisibility)
        window.removeEventListener("popstate", onPopState)
      }
    }, [])

    const setToasterMessage = async () => {
      if (!_isEmpty(toaster?.content)) {
        const { successMessage, unfavoriteSuccessMessage } = toaster.content
        setToasterContent({
          successMessage: await getShortenedUrl(successMessage),
          unfavoriteSuccessMessage: await getShortenedUrl(
            unfavoriteSuccessMessage
          ),
        })
      }
    }
    const handleSaveToListSuccessCallBack = () => {
      setProductSavedToList(true)
    }

    // Modified: Store both event and sku in loginFavouriteCallback, add isPostLoginRetry flag
    const handleLogin = (
      e,
      setLoginFavouriteCallback,
      dispatch,
      setAuthModalVisibility,
      sku,
      productInfo,
      isPostLoginRetry = false
    ) => {
      e.preventDefault()
      e.stopPropagation()
      if (!isPostLoginRetry) {
        dataLayerSavetolistGuest(productInfo)
      }
      setLoginFavouriteCallback({ event: e, sku, isPostLoginRetry })
      dispatch(setAuthModalVisibility({ show: true }))
    }

    // Modified: Accepts either (e, sku) or ({event, sku, isPostLoginRetry})
    const handleFavorite = (
      eOrObj,
      skuArg,
      productInfo,
      getAnalyticsInfoOnclick
    ) => {
      let e
      let sku
      let isPostLoginRetry = false
      if (
        eOrObj &&
        typeof eOrObj === "object" &&
        "event" in eOrObj &&
        "sku" in eOrObj
      ) {
        e = eOrObj.event
        sku = eOrObj.sku
        isPostLoginRetry = !!eOrObj.isPostLoginRetry
      } else {
        e = eOrObj
        sku = skuArg
      }
      if (!e || !sku) return
      window.preActiveElement = e.target
      if (isAuthenticated) {
        if (isSaveProductToList) {
          if (userListItemsSkus[sku]) {
            setOpenModal(true)
            setDeleteFromList(true)
            dataLayerHeartClickSaveListButton(
              productInfo,
              "delete from list",
              WISHLIST_DELETE
            )
            return
          }
          setShowAddToList(true)
          setLoginFavouriteCallback(null)
          // Only fire analytics for guest, not for logged-in user after login
          if (!isPostLoginRetry) {
            dataLayerHeartClickSaveListButton(
              productInfo,
              "save to list",
              WISHLIST_SAVE
            )
          }
          return
        }
      } else {
        // Only fire guest analytics on initial click, not on post-login retry
        handleLogin(
          e,
          setLoginFavouriteCallback,
          dispatch,
          setAuthModalVisibility,
          sku,
          productInfo,
          false // always false here, initial guest click
        )
        return
      }

      if (isFavorite === true) {
        favorites?.map(fav => {
          if (fav.productId === data["ctId_s"]) {
            if (!isAuthenticated) {
              handleLogin(
                e,
                setLoginFavouriteCallback,
                dispatch,
                setAuthModalVisibility,
                sku,
                productInfo,
                true // post-login retry
              )
            } else {
              getAnalyticsInfoOnclick(
                "favorites",
                "success",
                t("kf.favorites.removeItem")
              )
              dispatch(removeItems({ ids: [fav.id], isDataLayerNeeded: false }))
                .unwrap()
                .then(res => {
                  dispatch(
                    showToast({
                      message:
                        toasterContent.unfavoriteSuccessMessage ||
                        t("kf.favorites.removeItem"),
                      isVisible: true,
                    })
                  )
                })
                .catch(err => {
                  getAnalyticsInfoOnclick(
                    "favorites",
                    "failure",
                    t("kf.favorites.removeItemError")
                  )
                  dispatch(
                    showToast({
                      message: t("kf.favorites.removeItemError"),
                      isVisible: true,
                    })
                  )
                })
            }
          }
        })
      } else {
        if (!isAuthenticated) {
          handleLogin(
            e,
            setLoginFavouriteCallback,
            dispatch,
            setAuthModalVisibility,
            sku,
            productInfo,
            true // post-login retry
          )
        } else {
          dispatch(addProduct(sku))
            .unwrap()
            .then(() => {
              getAnalyticsInfoOnclick(
                "favorites",
                "success",
                t("kf.favorites.success")
              )
              dispatch(
                showToast({
                  message:
                    toasterContent.successMessage || t("kf.favorites.success"),
                  isVisible: true,
                })
              )
            })
            .catch(err => {
              getAnalyticsInfoOnclick(
                "favorites",
                "failure",
                t("kf.favorites.error")
              )
              dispatch(
                showToast({
                  message: t("kf.favorites.error"),
                  isVisible: true,
                })
              )
            })
        }
      }
      setLoginFavouriteCallback(null)
    }

    // Handle focus on save button to ensure it's visible past navigation menu
    const handleSaveButtonFocus = () => {
      if (!saveButtonRef.current) return

      // Determine row cols based on width (same logic as ProductList)
      // Get the element's position
      const rect = saveButtonRef.current.getBoundingClientRect()
      const navHeight = 100 // Approximate navigation menu height, adjust as needed

      // Check if element is hidden behind navigation
      if (rect.top < navHeight) {
        // Scroll to make the element visible with some offset
        const scrollOffset = window.pageYOffset + rect.top - navHeight - 20
        window.scrollTo({
          top: scrollOffset,
          behavior: "smooth",
        })
      }
    }

    useEffect(() => {
      if (loginFavouriteCallback && isAuthenticated) {
        // Prevent guest analytics after login
        const callback = {
          ...loginFavouriteCallback,
          isPostLoginRetry: true,
        }
        if (isSaveProductToList) {
          dispatch(getMyAccountListsWithLineItems())
            .unwrap()
            .then(res => {
              const userlistSKUS = res[0][1]
              if (userlistSKUS[data["sku_s"]]) {
                setOpenModal(true)
                setDeleteFromList(true)
              } else {
                handleFavorite(callback)
              }
            })
        } else {
          handleFavorite(callback)
        }
      }
    }, [loginFavouriteCallback, isAuthenticated])

    useEffect(() => {
      const props = getProductTabData({
        data,
        isSterling,
        globalConfig,
        persona,
        handleOnClickPartsDiagramLink,
        compareData,
        compareErrorMessage,
        setCompareErrorMessage,
        compareChecked,
        setCompareChecked,
        favIconState,
        handleFavorite,
        swatchCount,
        setColorSku,
        colorSku,
        index,
        cartData,
        locale,
        searchResultDataLayerObj,
        brandSwitcherItems,
        type,
        width,
        paginationState,
        setCardLoader,
        setShowQuickViewModal,
        setScrollY,
        t,
        saveButtonRef,
        handleSaveButtonFocus,
      })
      setMainProps(props)
    }, [
      compareChecked,
      compareErrorMessage,
      compareData,
      data,
      userListItemsSkus,
      favIconState,
      colorSku,
      stableSwatchCount,
    ])

    useEffect(() => {
      let timeoutId
      if (compareErrorMessage) {
        timeoutId = setTimeout(() => {
          setCompareErrorMessage("")
        }, 2000)
      }
      return () => clearTimeout(timeoutId)
    }, [compareErrorMessage])

    useEffect(() => {
      const isCurrentlyFavorite = userListItemsSkus[data.sku_s] ? true : null
      const newIsSaved = getFavIconStatus(
        isSaveProductToList,
        productSavedToList,
        { current: userListItemsSkus },
        { current: favorites },
        data.sku_s,
        data.ctId_s,
        isCurrentlyFavorite
      )
      setFavIconState(newIsSaved)
      setIsFavorite(
        favorites?.find(vl => vl.productId === data["ctId_s"]) ? true : false
      )
    }, [
      favIconState,
      showAddToList,
      openModal,
      deleteFromList,
      userListItemsSkus,
      favorites,
      isFavorite,
      isAuthenticated,
      data,
    ])

    useEffect(() => {
      if (
        compareData?.comparedProducts.find(
          prod => prod.compareSku === data.sku_s
        ) &&
        compareErrorMessage === ""
      ) {
        setCompareChecked(true)
      } else {
        setCompareChecked(false)
      }
    }, [compareData, data, compareErrorMessage])

    const handleQuickViewHide = async () => {
      if (scrollY) {
        window.scrollTo({ top: parseInt(scrollY, 10), behavior: "instant" })
      }
      setScrollY(0)
      setShowQuickViewModal(false)
      sessionStorage.removeItem("plpScrollSku") // back=K-T13140-4A-CP
      sessionStorage.removeItem("plpCurrentRows")
      mainProps.getAnalyticsInfoOnclick("quick_view_close")
    }

    return (
      <div className="product-list-tile__wrapper wow">
        {cardLoader && (
          <div className="spinner-overlay">
            <Spinner mod="white" />
          </div>
        )}
        {isSterling ? (
          <Typography size={"h5"} className="family-of-brands">
            {t("kf.viewFamilyResults")}
          </Typography>
        ) : null}
        <WrappedComponent {...mainProps.productTabProps} />
        {isSterling ? <Button {...mainProps.sterlingButtonProps} /> : null}
        {openModal ? (
          <ListConfirmationModal
            openConforMationModal={openModal}
            deleteFromList={deleteFromList}
            closeConformationModal={() => setOpenModal(false)}
            list={userListItemsSkus[data.sku_s]}
            deleteConfirmationModal={() => setProductSavedToList(false)}
          />
        ) : showAddToList ? (
          <SaveToList
            productSku={data.sku_s}
            showListSaveAndCreate={showAddToList}
            listSaveAndCreateClose={() => {
              setShowAddToList(false)
            }}
            getProductInfo={mainProps?.productInfo ?? {}}
            handleSuccessCallBack={handleSaveToListSuccessCallBack}
          />
        ) : null}
        {showQuickViewModal ? (
          <Modal
            showModal={showQuickViewModal}
            onModalClose={handleQuickViewHide}
            className="quickview-modal"
            fromPLP={true}
            fromSearch={true}
          >
            <QuickView
              productData={data}
              closeModal={handleQuickViewHide}
              authorData={quickViewAuthData}
              quickViewSelectedSku={data.sku_s}
              plp={true}
              search={true}
            />
          </Modal>
        ) : null}
      </div>
    )
  }
}

export default productTileHoc
